import React, { createContext, useContext, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Capacitor } from '@capacitor/core';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { getApiBase } from '../utils/apiBase';
import { t } from '../locales';

const AuthContext = createContext(null);

const API_URL = getApiBase() + '/api';

const AUTH_DEBUG =
  typeof process !== 'undefined' &&
  process.env &&
  String(process.env.REACT_APP_AUTH_DEBUG || '').toLowerCase() === 'true';

function authDebug(...args) {
  if (AUTH_DEBUG) console.info('[auth]', ...args);
}

/** Axios Headers nesnesini düz objeye çevirir (log için). */
function plainResponseHeaders(h) {
  if (!h) return null;
  try {
    if (typeof h.toJSON === 'function') return h.toJSON();
  } catch {
    /* ignore */
  }
  if (typeof h === 'object') return { ...h };
  return h;
}

/** Log için Authorization değerini maskele. */
function redactHeadersForLog(h) {
  if (!h || typeof h !== 'object') return h;
  const out = { ...h };
  const a = out.Authorization ?? out.authorization;
  if (typeof a === 'string' && a.startsWith('Bearer ')) {
    out.Authorization = 'Bearer ***';
    delete out.authorization;
  }
  return out;
}

/**
 * Tarayıcıda document.cookie: yalnızca HttpOnly olmayan çerezler görünür.
 * API çerezi genelde HttpOnly + farklı origin → burada görünmez; yine de teşhis için isim listesi.
 */
function cookieDiagnosticsForLog() {
  if (typeof document === 'undefined') {
    return { documentCookieReadable: false };
  }
  const dc = document.cookie || '';
  const names = dc
    .split(';')
    .map((s) => s.split('=')[0].trim())
    .filter(Boolean);
  return {
    documentCookieLength: dc.length,
    documentCookieNames: names.slice(0, 25),
    note:
      'Same-origin document.cookie; HttpOnly veya başka site çerezleri burada görünmez. Kesin Set-Cookie için DevTools → Network.',
  };
}

/**
 * CORS nedeniyle Set-Cookie çoğu zaman JS’e expose edilmez; expose edilmişse özetler.
 */
function setCookieHeaderSummary(axiosHeaders) {
  const plain = plainResponseHeaders(axiosHeaders);
  if (!plain || typeof plain !== 'object') {
    return {
      setCookieVisibleToJavaScript: false,
      hint: 'Tarayıcı cross-origin yanıtta Set-Cookie’yi JS’e vermeyebilir; Network sekmesinde kontrol edin.',
    };
  }
  const key = Object.keys(plain).find((k) => k.toLowerCase() === 'set-cookie');
  if (!key) {
    return {
      setCookieVisibleToJavaScript: false,
      exposedHeaderKeys: Object.keys(plain).slice(0, 20),
      hint: 'Set-Cookie yok veya CORS ile expose edilmedi.',
    };
  }
  const raw = plain[key];
  const preview = Array.isArray(raw)
    ? raw.map((c) => (typeof c === 'string' ? `${c.split(';')[0]};…` : String(c)))
    : typeof raw === 'string'
      ? `${raw.split(';')[0]};…`
      : raw;
  return { setCookieVisibleToJavaScript: true, setCookiePreview: preview };
}

/** Oturum gerektirmeyen veya checkAuth’ın özel işlediği sayfalar */
function isAuthPublicLocation(pathname) {
  if (!pathname) return true;
  if (['/login', '/register', '/auth/callback', '/forgot-password', '/privacy'].includes(pathname)) {
    return true;
  }
  if (pathname.startsWith('/reset-password')) return true;
  return false;
}

/** FastAPI { detail: { error_code } } veya düz gövde */
function parseAuthErrorCode(data) {
  if (!data || typeof data !== 'object') return null;
  const d = data.detail;
  if (d && typeof d === 'object' && d.error_code) return d.error_code;
  if (data.error_code) return data.error_code;
  return null;
}

function createAuthFlowError(message, extras = {}) {
  const err = new Error(message);
  Object.assign(err, extras);
  return err;
}

function isNativeAuthClient() {
  return Capacitor.isNativePlatform();
}

function isMobileBrowserAuthClient() {
  if (Capacitor.isNativePlatform()) return false;
  if (typeof navigator === 'undefined') return false;
  const ua = String(navigator.userAgent || '');
  // Simplified regex - only detect actual mobile devices, no false positives
  return /(Android|iPhone|iPad|iPod)/i.test(ua);
}

function resolveClientPlatform() {
  if (isNativeAuthClient()) return 'native';
  if (isMobileBrowserAuthClient()) return 'mobile-browser';
  return 'desktop-browser';
}

/** Mobilde bearer token localStorage'da tutulur; web cookie-first çalışır. */
function readStoredToken() {
  try {
    return localStorage.getItem('token');
  } catch {
    return null;
  }
}

function persistStoredToken(token) {
  try {
    localStorage.setItem('token', token);
  } catch {
    /* ignore */
  }
}

function clearStoredToken() {
  try {
    localStorage.removeItem('token');
  } catch {
    /* ignore */
  }
  delete axios.defaults.headers.common['Authorization'];
}

function clientTypeHeaders(authMode) {
  if (authMode === 'native-bearer') return { 'X-Client-Type': 'native' };
  if (authMode === 'mobile-browser-bearer') return { 'X-Client-Type': 'mobile' };
  return {};
}

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const clientPlatform = resolveClientPlatform();
  const isMobileBrowser = clientPlatform === 'mobile-browser';
  const nativeAuth = clientPlatform === 'native';

  // Dynamic auth mode that can change at runtime based on behavior
  const [authMode, setAuthMode] = React.useState(() => {
    if (nativeAuth) return 'native-bearer';
    if (isMobileBrowser) return 'mobile-browser-cookie-probe';
    return 'web-cookie';
  });

  const [bearerAuth, setBearerAuth] = React.useState(nativeAuth);
  const [user, setUser] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [isAuthResolved, setIsAuthResolved] = React.useState(false);
  const [avatarOnboardingRequired, setAvatarOnboardingRequired] = React.useState(false);
  const [avatarOnboardingChecked, setAvatarOnboardingChecked] = React.useState(false);
  const [avatarCandidates, setAvatarCandidates] = React.useState([]);
  const [avatarCandidatesLoading, setAvatarCandidatesLoading] = React.useState(false);
  const authRedirectLockRef = useRef(0);
  const authBootstrapStartedRef = useRef(false);
  /** Oturum doğrulama nesli: login/OAuth sonrası artırılır; eski checkAuth sonuçları yok sayılır (401 yarışı). */
  const authEpochRef = useRef(0);
  const lastAuthFailureRef = useRef(null);
  const pendingBearerTokenRef = useRef(null);
  const authModeReasonRef = useRef('platform_default');
  const userRef = useRef(null);

  React.useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Dynamic auth mode updater based on behavior
  const updateAuthMode = useCallback((newMode, reason) => {
    authDebug('auth mode update', { 
      from: authMode, 
      to: newMode, 
      reason,
      clientPlatform 
    });
    setAuthMode(newMode);
    setBearerAuth(newMode === 'native-bearer' || newMode === 'mobile-browser-bearer');
    authModeReasonRef.current = reason;
  }, [authMode, clientPlatform]);

  useEffect(() => {
    authDebug('auth mode active', {
      clientPlatform,
      authMode,
      reason: authModeReasonRef.current,
      bearerAuth,
    });
  }, [clientPlatform, authMode, bearerAuth]);

  const clearSessionClient = useCallback(() => {
    clearStoredToken();
    pendingBearerTokenRef.current = null;
    authModeReasonRef.current = 'session_cleared';
    // Reset to platform default on session clear
    if (nativeAuth) {
      updateAuthMode('native-bearer', 'session_cleared_native');
    } else if (isMobileBrowser) {
      updateAuthMode('mobile-browser-cookie-probe', 'session_cleared_mobile');
    } else {
      updateAuthMode('web-cookie', 'session_cleared_desktop');
    }
    setUser(null);
    setIsAuthResolved(false);
    setAvatarOnboardingRequired(false);
    setAvatarOnboardingChecked(true);
    lastAuthFailureRef.current = null;
  }, [nativeAuth, isMobileBrowser, updateAuthMode]);

  const enableMobileBrowserBearerFallback = useCallback(
    (reason, tokenCandidate) => {
      if (!isMobileBrowser) return null;
      const token = tokenCandidate || pendingBearerTokenRef.current || readStoredToken();
      if (!token) {
        authDebug('mobile browser bearer fallback unavailable', { reason });
        return null;
      }
      pendingBearerTokenRef.current = token;
      updateAuthMode('mobile-browser-bearer', reason);
      authDebug('mobile browser bearer fallback enabled', { reason });
      return token;
    },
    [isMobileBrowser, updateAuthMode]
  );

  /**
   * checkAuth ve benzeri 401 yanıtlarında: error_code’a göre yönlendirme / toast state.
   * Sonsuz döngü: aynı anda tekrar yönlendirmeyi kısa süre kilitle; public sayfada missing_token’da no-op.
   */
  const handleAuthError = useCallback(
    (error) => {
      const status = error?.response?.status;
      if (status !== 401) return;

      const code = parseAuthErrorCode(error?.response?.data);
      const path = typeof window !== 'undefined' ? window.location.pathname : '';
      const search = typeof window !== 'undefined' ? window.location.search || '' : '';
      const from = { pathname: path, search };

      authDebug('handleAuthError', { code, path, status });

      const now = Date.now();
      if (now - authRedirectLockRef.current < 350) {
        authDebug('handleAuthError skipped (debounce)');
        return;
      }
      authRedirectLockRef.current = now;

      if (code === 'missing_token') {
        if (isAuthPublicLocation(path)) return;
        navigate('/login', { replace: true, state: { from } });
        return;
      }

      if (code === 'expired_token' || code === 'invalid_token') {
        void axios.post(`${API_URL}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
        clearSessionClient();
        if (path === '/login') {
          toast.error(t('auth.sessionExpired'), { id: 'auth-dekosta-session-expired' });
          return;
        }
        navigate('/login', {
          replace: true,
          state: { from, authToastId: 'session_expired' },
        });
        return;
      }

      if (code === 'user_not_found') {
        void axios.post(`${API_URL}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
        clearSessionClient();
        if (path === '/login') {
          toast.error(t('auth.userNotFound'), { id: 'auth-dekosta-user-not-found' });
          return;
        }
        navigate('/login', {
          replace: true,
          state: { from, authToastId: 'user_not_found' },
        });
        return;
      }

      if (!isAuthPublicLocation(path) && path !== '/login') {
        navigate('/login', { replace: true, state: { from } });
      }
    },
    [navigate, clearSessionClient]
  );

  useEffect(() => {
    const token = bearerAuth ? readStoredToken() : null;
    if (token && bearerAuth) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }

    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        const tok = bearerAuth ? readStoredToken() : null;
        const hasAuthHeader =
          Boolean(config.headers?.Authorization) || Boolean(config.headers?.authorization);
        if (tok && bearerAuth && config.url?.includes(API_URL) && !hasAuthHeader) {
          config.headers['Authorization'] = `Bearer ${tok}`;
        }
        config.withCredentials = true;
        return config;
      },
      (e) => Promise.reject(e)
    );

    const responseInterceptor = axios.interceptors.response.use(
      (res) => res,
      (err) => {
        if (err.response?.status === 401 && err.config?.url?.includes(API_URL)) {
          const url = err.config?.url || '';
          const skipSessionClear =
            url.includes('/auth/me') ||
            url.includes('/me/avatar') ||
            url.includes('/me/avatars');
          if (!skipSessionClear && isAuthResolved) {
            if (bearerAuth) {
              clearStoredToken();
            }
            setUser(null);
            setIsAuthResolved(false);
            setAvatarOnboardingRequired(false);
            setAvatarOnboardingChecked(true);
          }
        }
        return Promise.reject(err);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [isAuthResolved, bearerAuth]);

  const refreshAvatarOnboardingStatus = useCallback(async (epochAtStart, options = {}) => {
    const epoch = epochAtStart !== undefined ? epochAtStart : authEpochRef.current;
    /** checkAuth içinden çağrıda setIsAuthResolved(true) henüz aynı turda closure'a yansımaz; gate atlanmalı. */
    const skipAuthResolvedGate = Boolean(options.skipAuthResolvedGate);
    if (!skipAuthResolvedGate && !isAuthResolved) {
      authDebug('refreshAvatarOnboardingStatus skipped - auth not resolved');
      return null;
    }
    try {
      const res = await axios.get(`${API_URL}/me/avatar/onboarding-status`, { withCredentials: true });
      if (epoch !== authEpochRef.current) return null;
      const required = Boolean(res?.data?.required);
      setAvatarOnboardingRequired(required);
      setAvatarOnboardingChecked(true);
      return required;
    } catch (e) {
      if (epoch !== authEpochRef.current) return null;
      const st = e?.response?.status;
      if (st === 401) {
        setAvatarOnboardingChecked(true);
        return null;
      }
      setAvatarOnboardingChecked(true);
      return null;
    }
  }, [isAuthResolved]);

  const loadAvatarCandidates = useCallback(async () => {
    if (!userRef.current || !isAuthResolved) {
      authDebug('loadAvatarCandidates skipped - auth not resolved or no user');
      setAvatarCandidates([]);
      return [];
    }
    setAvatarCandidatesLoading(true);
    try {
      const res = await axios.get(`${API_URL}/me/avatars`, { withCredentials: true });
      if (!userRef.current) {
        setAvatarCandidates([]);
        return [];
      }
      const items = Array.isArray(res?.data?.items) ? res.data.items : [];
      setAvatarCandidates(items);
      return items;
    } catch {
      setAvatarCandidates([]);
      return [];
    } finally {
      setAvatarCandidatesLoading(false);
    }
  }, [isAuthResolved]);

  const checkAuth = useCallback(
    async (options = {}) => {
      const epochAtStart = authEpochRef.current;
      const force = Boolean(options.force);

      if (!force && userRef.current && isAuthResolved) {
        authDebug('checkAuth skipped (already resolved)');
        return userRef.current;
      }

      setLoading(true);
      let requestTransport = 'cookie';
      let headers = { ...clientTypeHeaders(authMode) };

      if (options.transport === 'bearer') {
        requestTransport = 'bearer';
        const tok =
          options.bearerToken ||
          options.fallbackToken ||
          pendingBearerTokenRef.current ||
          readStoredToken();
        if (tok) {
          headers.Authorization = `Bearer ${tok}`;
        }
      } else if (nativeAuth || bearerAuth) {
        requestTransport = 'bearer';
        const tok =
          pendingBearerTokenRef.current || options.fallbackToken || readStoredToken();
        if (tok) {
          headers.Authorization = `Bearer ${tok}`;
        }
      }

      const meUrl = `${API_URL}/auth/me`;
      authDebug('checkAuth GET /auth/me request', {
        apiBase: getApiBase(),
        requestUrl: meUrl,
        withCredentials: true,
        transport: requestTransport,
        requestHeaders: redactHeadersForLog(headers),
        note:
          'Tarayıcı Cookie istek başlığını JS’e göstermez; withCredentials:true ile gönderilir.',
        cookieDiagnostics: cookieDiagnosticsForLog(),
      });

      try {
        const res = await axios.get(meUrl, {
          withCredentials: true,
          headers,
        });

        if (epochAtStart !== authEpochRef.current) return null;

        const u = res?.data;
        setUser(u);
        setIsAuthResolved(true);
        lastAuthFailureRef.current = null;
        void refreshAvatarOnboardingStatus(epochAtStart, { skipAuthResolvedGate: true });
        void loadAvatarCandidates();
        // Push notification token register (native build; web no-op)
        try {
          const { initPushNotifications } = await import('../utils/pushNotifications');
          void initPushNotifications();
        } catch (_) {
          // plugin yoksa sessiz geç
        }
        return u;
      } catch (error) {
        if (epochAtStart !== authEpochRef.current) return null;

        const status = error?.response?.status;
        const data = error?.response?.data;
        const code = parseAuthErrorCode(data);
        lastAuthFailureRef.current = { status, data, error_code: code };

        authDebug('checkAuth GET /auth/me error', {
          apiBase: getApiBase(),
          requestUrl: meUrl,
          authMode,
          transport: requestTransport,
          withCredentials: true,
          requestHeaders: redactHeadersForLog(headers),
          status,
          error_code: code,
          responseBody: data,
          responseHeaders: plainResponseHeaders(error?.response?.headers),
          cookieDiagnostics: cookieDiagnosticsForLog(),
        });

        if (
          requestTransport === 'cookie' &&
          status === 401 &&
          code === 'missing_token' &&
          isMobileBrowser &&
          !nativeAuth
        ) {
          const fallbackToken = enableMobileBrowserBearerFallback(
            `cookie_missing_token_${code}`,
            options.fallbackToken
          );

          if (fallbackToken) {
            authDebug('checkAuth retrying with bearer fallback', {
              previous_transport: 'cookie',
              error_code: code,
            });

            return checkAuth({
              force: true,
              transport: 'bearer',
              bearerToken: fallbackToken,
              fallbackReason: options.fallbackReason,
              fallbackToken: options.fallbackToken,
            });
          }
        }

        if (status === 401 || status === 403) {
          if (bearerAuth) {
            clearStoredToken();
          }
          setUser(null);
          setIsAuthResolved(true);
          setAvatarOnboardingRequired(false);
          setAvatarOnboardingChecked(true);
          if (status === 401) {
            handleAuthError(error);
          }
        } else {
          setUser(null);
          setIsAuthResolved(true);
          setAvatarOnboardingRequired(false);
          setAvatarOnboardingChecked(true);
        }
        return null;
      } finally {
        setLoading(false);
      }
    },
    [
      refreshAvatarOnboardingStatus,
      loadAvatarCandidates,
      handleAuthError,
      isAuthResolved,
      authMode,
      bearerAuth,
      nativeAuth,
      isMobileBrowser,
      enableMobileBrowserBearerFallback,
    ]
  );

  const selectAvatar = useCallback(
    async (avatarId) => {
      const res = await axios.post(
        `${API_URL}/me/avatar/select`,
        { avatar_id: avatarId },
        { withCredentials: true }
      );
      // Backend kesin yanıt: onboarding bitti → flag'i HEMEN kapat (refresh race'ine güvenme).
      // Aksi halde fire-and-forget refreshAvatarOnboardingStatus epoch yarışında modal açık kalıyordu.
      if (res?.data?.avatar_onboarding_required === false) {
        setAvatarOnboardingRequired(false);
        setAvatarOnboardingChecked(true);
      }
      authEpochRef.current += 1;
      await checkAuth({ force: true });
    },
    [checkAuth]
  );

  const acceptLegal = useCallback(async () => {
    const headers = { ...clientTypeHeaders(authMode) };
    if (bearerAuth) {
      const tok = readStoredToken();
      if (tok) headers.Authorization = `Bearer ${tok}`;
    }
    await axios.post(
      `${API_URL}/me/accept-legal`,
      { age_confirmed: true, kvkk_accepted: true, terms_accepted: true },
      { withCredentials: true, headers }
    );
    authEpochRef.current += 1;
    // force: true — yasal onay sonrası user'ı kesin yenile (gate kapansın; force'suz dedup'a takılıyordu)
    await checkAuth({ force: true });
  }, [authMode, bearerAuth, checkAuth]);

  const completeOnboarding = useCallback(async () => {
    // Native/bearer: X-Client-Type + Authorization bearer'ı açıkça ekle ki backend yazımı %100 tutsun.
    const headers = { ...clientTypeHeaders(authMode) };
    if (bearerAuth) {
      const tok = readStoredToken();
      if (tok) headers.Authorization = `Bearer ${tok}`;
    }
    await axios.post(
      `${API_URL}/me/onboarding/complete`,
      { completed: true },
      { withCredentials: true, headers }
    );
    authEpochRef.current += 1;
    await checkAuth({ force: true });
  }, [authMode, bearerAuth, checkAuth]);

  const finalizeOAuthToken = useCallback(
    async (token) => {
      if (!token || typeof token !== 'string') return null;
      clearStoredToken();
      persistStoredToken(token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      authEpochRef.current += 1;
      authDebug('finalizeOAuthToken token stored, checkAuth');
      return checkAuth({ force: true });
    },
    [checkAuth]
  );

  useEffect(() => {
    if (authBootstrapStartedRef.current) return;
    authBootstrapStartedRef.current = true;
    void checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    authDebug('login POST start', {
      apiBase: getApiBase(),
      loginUrl: `${API_URL}/auth/login`,
      email,
      authMode,
    });
    try {
      let response = await axios.post(
        `${API_URL}/auth/login`,
        { email, password },
        { withCredentials: true, headers: clientTypeHeaders(authMode) }
      );
      let token = response.data?.token;
      let userFromLogin = response.data?.user;
      /** Closure’daki bearerAuth güncellenmeden önce: gövde tokenı zorunlu mu (native veya mobil bearer)? */
      let bodyTokenRequired = nativeAuth || authMode === 'mobile-browser-bearer';
      authDebug('login POST success', {
        status: response.status,
        responseBodyKeys: Object.keys(response.data || {}),
        hasTokenInBody: Boolean(token),
        userId: userFromLogin?.user_id,
        authMode,
      });

      if (!token && authMode === 'web-cookie') {
        authDebug('login using web-cookie mode (no token expected)');
      } else if (!token && (authMode === 'native-bearer' || authMode === 'mobile-browser-bearer')) {
        authDebug('login token missing in bearer mode, retrying with cookie-first client headers', {
          authMode,
        });
        const wasMobileBearer = authMode === 'mobile-browser-bearer' && isMobileBrowser;
        updateAuthMode(
          wasMobileBearer ? 'mobile-browser-cookie-probe' : 'web-cookie',
          'login_retry_missing_token'
        );
        if (wasMobileBearer) {
          bodyTokenRequired = false;
        }
        response = await axios.post(
          `${API_URL}/auth/login`,
          { email, password },
          {
            withCredentials: true,
            headers: clientTypeHeaders(wasMobileBearer ? 'mobile-browser-cookie-probe' : 'web-cookie'),
          }
        );
        token = response.data?.token;
        userFromLogin = response.data?.user ?? userFromLogin;
        authDebug('login retry success', {
          status: response.status,
          hasTokenInBody: Boolean(token),
        });
      }

      authDebug('login POST response (network)', {
        apiBase: getApiBase(),
        loginUrl: `${API_URL}/auth/login`,
        status: response.status,
        responseHeaders: plainResponseHeaders(response.headers),
        setCookie: setCookieHeaderSummary(response.headers),
        responseData: response.data,
        cookieDiagnostics: cookieDiagnosticsForLog(),
      });

      if (bodyTokenRequired) {
        clearStoredToken();
      }
      pendingBearerTokenRef.current = isMobileBrowser ? token || null : null;

      if (bodyTokenRequired && !token) {
        throw createAuthFlowError(t('auth.sessionVerifyFailed'), {
          stage: 'bearer_login_token_missing',
          loginResponse: {
            status: response.status,
            data: response.data,
          },
        });
      }

      if (bodyTokenRequired) {
        pendingBearerTokenRef.current = token || null;
      }

      if (bodyTokenRequired && token) {
        persistStoredToken(token);
        authDebug('login token stored in localStorage');
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        authDebug('login Authorization header set');
      } else if (isMobileBrowser) {
        delete axios.defaults.headers.common['Authorization'];
        authDebug('mobile browser login will try cookie before bearer fallback', {
          hasTokenInBody: Boolean(token),
        });
      }
      
      authEpochRef.current += 1;
      authDebug('login epoch incremented, calling checkAuth');
      const me = await checkAuth({
        force: true,
        fallbackToken: isMobileBrowser ? token || null : null,
        fallbackReason: 'mobile_browser_login_cookie_missing',
      });
      
      if (!me) {
        const authFailure = lastAuthFailureRef.current;
        authDebug('login checkAuth failed after POST success', authFailure || {});
        throw createAuthFlowError(t('auth.sessionVerifyFailed'), {
          stage: 'auth_me_after_login',
          loginResponse: {
            status: response.status,
            data: response.data,
          },
          response: authFailure
            ? {
                status: authFailure.status,
                data: authFailure.data,
              }
            : undefined,
          authCheck: authFailure,
        });
      }
      
      authDebug('login flow completed successfully', { userId: me.user_id });
      return me;
    } catch (error) {
      const res = error?.response;
      authDebug('login failed (gerçek ağ yanıtı)', {
        apiBase: getApiBase(),
        loginUrl: `${API_URL}/auth/login`,
        status: res?.status,
        responseData: res?.data,
        responseHeaders: plainResponseHeaders(res?.headers),
        error_code: parseAuthErrorCode(res?.data),
        message: error?.message,
        code: error?.code,
        authMode,
        request: error?.config
          ? {
              url: error.config.url,
              method: error.config.method,
              withCredentials: error.config.withCredentials,
              headers: redactHeadersForLog(plainResponseHeaders(error.config.headers) || {}),
            }
          : null,
      });
      throw error;
    }
  };

  const appleSignIn = async ({ identityToken, user, legal = {} }) => {
    if (!identityToken) {
      throw new Error('Apple identity_token missing');
    }
    const response = await axios.post(
      `${API_URL}/auth/apple`,
      {
        identity_token: identityToken,
        user: user || null,
        age_confirmed: !!legal.ageConfirmed,
        kvkk_accepted: !!legal.kvkkAccepted,
        terms_accepted: !!legal.termsAccepted,
      },
      { withCredentials: true, headers: clientTypeHeaders(authMode) }
    );
    const data = response.data;
    const token = data?.token;
    if (bearerAuth) clearStoredToken();
    pendingBearerTokenRef.current = isMobileBrowser ? token || null : null;
    // Native/bearer: token'ı sakla + Authorization header set et (login/register ile aynı).
    // Eksikti → native'de Apple login token'ı atılıyor, checkAuth oturumsuz kalıyordu.
    if (bearerAuth && !token) {
      throw new Error('Apple ile giriş: oturum tokenı alınamadı.');
    }
    if (bearerAuth && token) {
      persistStoredToken(token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    authEpochRef.current += 1;
    await checkAuth();
    return data?.user;
  };

  const register = async (email, password, legal = {}) => {
    const response = await axios.post(
      `${API_URL}/auth/register`,
      {
        email,
        password,
        age_confirmed: !!legal.ageConfirmed,
        kvkk_accepted: !!legal.kvkkAccepted,
        terms_accepted: !!legal.termsAccepted,
      },
      { withCredentials: true, headers: clientTypeHeaders(authMode) }
    );
    const data = response.data;
    const token = data?.token;
    const userFromRegister = data?.user;
    if (!userFromRegister) {
      throw new Error(t('auth.unexpectedResponse'));
    }
    authEpochRef.current += 1;
    authDebug('register POST success', {
      status: response.status,
      responseBodyKeys: Object.keys(data || {}),
      hasTokenInBody: Boolean(token),
      userId: userFromRegister?.user_id,
      authMode,
    });
    if (bearerAuth) {
      clearStoredToken();
    }
    pendingBearerTokenRef.current = isMobileBrowser ? token || null : null;
    if (bearerAuth && !token) {
      throw createAuthFlowError(t('auth.sessionVerifyFailed'), {
        stage: 'bearer_register_token_missing',
        registerResponse: {
          status: response.status,
          data,
        },
      });
    }
    if (bearerAuth && token) {
      persistStoredToken(token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else if (isMobileBrowser) {
      delete axios.defaults.headers.common['Authorization'];
      authDebug('mobile browser register will try cookie before bearer fallback', {
        hasTokenInBody: Boolean(token),
      });
    }
    const me = await checkAuth({
      force: true,
      fallbackToken: isMobileBrowser ? token || null : null,
      fallbackReason: 'mobile_browser_register_cookie_missing',
    });
    if (!me) {
      const authFailure = lastAuthFailureRef.current;
      throw createAuthFlowError(t('auth.sessionVerifyFailed'), {
        stage: 'auth_me_after_register',
        registerResponse: {
          status: response.status,
          data,
        },
        response: authFailure
          ? {
              status: authFailure.status,
              data: authFailure.data,
            }
          : undefined,
        authCheck: authFailure,
      });
    }
    return me;
  };

  const logout = async () => {
    authEpochRef.current += 1;
    try {
      await axios.post(`${API_URL}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    pendingBearerTokenRef.current = null;
    if (nativeAuth || isMobileBrowser) {
      clearStoredToken();
      authModeReasonRef.current = 'logout_reset';
      if (isMobileBrowser) {
        updateAuthMode('mobile-browser-cookie-probe', 'logout_reset');
      }
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
    setUser(null);
    setAvatarOnboardingRequired(false);
    setAvatarOnboardingChecked(true);
  };

  const isAuthenticated = Boolean(user);
  const authLoading = loading;

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        authLoading: loading,
        isAuthResolved,
        isAuthenticated: Boolean(user),
        authMode,
        login,
        register,
        appleSignIn,
        logout,
        checkAuth,
        finalizeOAuthToken,
        avatarOnboardingRequired,
        avatarOnboardingChecked,
        avatarCandidates,
        avatarCandidatesLoading,
        loadAvatarCandidates,
        selectAvatar,
        completeOnboarding,
        acceptLegal,
        refreshAvatarOnboardingStatus,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
